import { useEffect, useMemo, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { CheckCircle2, Copy, ExternalLink, MessageCircleMore, Send } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { api } from '@/api/client'
import { SideSheet } from '@/components/overlay-panels'
import { Badge, Button } from '@/components/ui'
import type { MessengerLinkPayload, MessengerLinkStatusPayload } from '@/types/api'

type MessengerKind = 'telegram' | 'max'

type MessengerMeta = {
  title: string
  badgeLabel: string
  badgeTone: 'accent' | 'success'
  buttonLabel: string
  botButtonLabel: string
  connectedToast: string
  idleText: string
  icon: LucideIcon
  openBotUrl: (payload: MessengerLinkPayload) => string
  steps: (payload: MessengerLinkPayload) => string[]
}

const MESSENGER_META: Record<MessengerKind, MessengerMeta> = {
  telegram: {
    title: 'Telegram',
    badgeLabel: 'Личный чат или группа',
    badgeTone: 'accent',
    buttonLabel: 'Telegram',
    botButtonLabel: 'Открыть бота',
    connectedToast: 'Telegram успешно подключён',
    idleText: 'Сгенерируйте токен и отправьте его боту, чтобы подключить личный чат или группу.',
    icon: Send,
    openBotUrl: (payload) => `${payload.botUrl}?start=${encodeURIComponent(payload.code)}`,
    steps: (payload) => [
      `Откройте бота @${payload.botUsername}.`,
      'Если нужен групповой чат, добавьте бота в нужную группу.',
      'Отправьте в нужный чат этот токен и дождитесь подтверждения привязки.',
    ],
  },
  max: {
    title: 'MAX',
    badgeLabel: 'Аккаунт или группа',
    badgeTone: 'success',
    buttonLabel: 'MAX',
    botButtonLabel: 'Открыть в MAX',
    connectedToast: 'MAX успешно подключён',
    idleText: 'Сгенерируйте токен и отправьте его боту MAX. Для группы бот должен быть администратором.',
    icon: MessageCircleMore,
    openBotUrl: (payload) => payload.botUrl,
    steps: (payload) => [
      `Откройте бота ${payload.botUsername.startsWith('@') ? payload.botUsername : `@${payload.botUsername}`}.`,
      'Подтвердите запуск бота в MAX или добавьте его в нужную группу.',
      'Отправьте в чат токен из этого окна и дождитесь подтверждения привязки.',
    ],
  },
}

function resolveCurrentLinkId(
  statusPayload: MessengerLinkStatusPayload | undefined,
  generatedPayload: MessengerLinkPayload | null,
) {
  return statusPayload?.linkedId ?? generatedPayload?.linkedId ?? null
}

async function copyText(value: string, successMessage: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(successMessage)
  } catch {
    toast.error('Не удалось скопировать значение')
  }
}

export function MessengerLinkSheet({
  kind,
  onClose,
  open,
}: {
  kind: MessengerKind
  onClose: () => void
  open: boolean
}) {
  const meta = MESSENGER_META[kind]
  const [generatedLink, setGeneratedLink] = useState<MessengerLinkPayload | null>(null)
  const [shouldPoll, setShouldPoll] = useState(false)
  const linkedToastShownRef = useRef(false)

  const statusQuery = useQuery({
    queryKey: ['messenger-link-status', kind],
    queryFn: () => (kind === 'telegram' ? api.telegramLinkStatus() : api.maxLinkStatus()),
    enabled: open,
    refetchInterval: open && shouldPoll ? 3000 : false,
  })

  const generateLinkMutation = useMutation({
    mutationFn: () => (kind === 'telegram' ? api.generateTelegramLink() : api.generateMaxLink()),
    onSuccess: (payload) => {
      linkedToastShownRef.current = false
      setGeneratedLink(payload)
      setShouldPoll(true)
      void statusQuery.refetch()
      toast.success('Код подключения готов')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  useEffect(() => {
    if (open) {
      return
    }

    linkedToastShownRef.current = false
    setGeneratedLink(null)
    setShouldPoll(false)
  }, [open])

  useEffect(() => {
    if (!generatedLink || statusQuery.data?.status !== 'linked') {
      return
    }

    setShouldPoll(false)

    if (!linkedToastShownRef.current) {
      toast.success(meta.connectedToast)
      linkedToastShownRef.current = true
    }
  }, [generatedLink, meta.connectedToast, statusQuery.data?.status])

  const currentStatus = statusQuery.data?.status ?? generatedLink?.status ?? 'waiting'
  const currentLinkedId = resolveCurrentLinkId(statusQuery.data, generatedLink)
  const currentIsGroup = statusQuery.data?.isGroup ?? generatedLink?.isGroup ?? false
  const openBotUrl = useMemo(
    () => (generatedLink ? meta.openBotUrl(generatedLink) : null),
    [generatedLink, meta],
  )
  const Icon = meta.icon

  return (
    <SideSheet
      description={`Подключите ${meta.title} к своему аккаунту CRM и используйте его параллельно с другим мессенджером.`}
      onClose={onClose}
      open={open}
      title={`Подключение ${meta.title}`}
      widthClassName="max-w-xl"
    >
      <div className="space-y-5">
        <div className="rounded-[26px] border border-ink-100 bg-slate-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white p-3 text-ink-900 shadow-soft">
                <Icon className="h-5 w-5" />
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-ink-950">{meta.title}</h3>
                  <Badge tone={meta.badgeTone}>{meta.badgeLabel}</Badge>
                </div>
                <p className="text-sm text-ink-500">
                  Статус: {currentStatus === 'linked' ? 'подключён' : 'ожидает подключения'}
                </p>
              </div>
            </div>

            <Button
              disabled={generateLinkMutation.isPending}
              onClick={() => generateLinkMutation.mutate()}
            >
              <Icon className="mr-2 h-4 w-4" />
              {currentStatus === 'linked' ? 'Получить новый токен' : 'Сгенерировать токен'}
            </Button>
          </div>

          <div className="mt-4 grid gap-3 rounded-[24px] border border-white/80 bg-white/80 p-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink-400">Текущий чат</p>
              <p className="mt-2 break-all text-sm font-semibold text-ink-900">
                {currentLinkedId ?? 'Пока не подключён'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink-400">Тип подключения</p>
              <p className="mt-2 text-sm font-semibold text-ink-900">
                {currentLinkedId ? (currentIsGroup ? 'Группа' : 'Личный чат') : 'Не определён'}
              </p>
            </div>
          </div>
        </div>

        {generatedLink ? (
          <div className="space-y-4 rounded-[26px] border border-accent-100 bg-accent-50/70 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
                  Одноразовый токен
                </p>
                <p className="mt-2 text-sm text-ink-500">
                  Токен действует {generatedLink.expiresInMinutes} минут.
                </p>
              </div>
              {openBotUrl ? (
                <a
                  className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-ink-900 shadow-soft transition hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  href={openBotUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {meta.botButtonLabel}
                </a>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <code className="rounded-2xl bg-white px-4 py-3 text-base font-semibold text-ink-950 shadow-soft">
                {generatedLink.code}
              </code>
              <Button
                onClick={() => copyText(generatedLink.code, 'Токен скопирован')}
                tone="ghost"
              >
                <Copy className="mr-2 h-4 w-4" />
                Скопировать
              </Button>
            </div>

            <ol className="space-y-2 text-sm text-ink-600">
              {meta.steps(generatedLink).map((step, index) => (
                <li key={`${kind}-step-${index + 1}`}>{index + 1}. {step}</li>
              ))}
            </ol>

            {currentStatus === 'linked' ? (
              <div className="flex items-center gap-3 rounded-[22px] border border-emerald-100 bg-white/85 px-4 py-3 text-sm font-medium text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                Привязка подтверждена, мессенджер готов к работе.
              </div>
            ) : (
              <p className="text-sm text-ink-500">
                Окно автоматически проверяет статус привязки, пока токен активен.
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-[26px] border border-ink-100 bg-white/80 p-4 text-sm text-ink-600">
            {meta.idleText}
          </div>
        )}
      </div>
    </SideSheet>
  )
}
